import React, { useState, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Text, Button } from '@cuemath/web-comps';
import { COLORS } from '@cuemath/comps';
import { parseBracketTimestamp, downloadTextFile } from 'utils';
import {
  PanelContainer,
  FilterButton,
  IssueHeader,
  TimestampButton,
  LabelBadge,
  ThemeBadge,
  IssueContent,
  ContentSection,
  ContentLabel,
  ContentText,
  AlternativeText,
  EmptyState,
  FilterModal,
  FilterHeader,
  FilterTitle,
  ClearButton,
  FilterCategory,
  FilterCategoryHeader,
  FilterCheckbox,
  FilterCategoryTitle,
  FilterCount,
  FilterOption,
  FilterWrapper,
  ActiveIssueCard,
  BadgesContainer,
  FilterLabel,
  FullWidthSelect,
  FilterButtonContainer,
  DomainContainer,
  DomainLabel,
  ThemeContainer,
  DomainHeader,
  ToggleIcon,
  DomainFilterOption,
} from './AIAnalysisPanel.styles';
import { ExportButton } from './PanelShared.styles';

const FILTER_DIALOG_TITLE_ID = 'ai-analysis-filter-dialog-title';

function AIAnalysisPanel({
  aiAnalysisData,
  videoTimeSec,
  onSeek,
  onGenerateAIAnalysis,
  isLoading,
  isProcessing,
  canGenerateAIAnalysis,
  classroomId,
  demoDate,
}) {
  const [expandedIssues, setExpandedIssues] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [expandedDomains, setExpandedDomains] = useState({});
  const [selectedDomains, setSelectedDomains] = useState([]);
  const [selectedThemes, setSelectedThemes] = useState([]);
  const [selectedSeverities, setSelectedSeverities] = useState([]);
  const [selectedAnalysisPass, setSelectedAnalysisPass] = useState('');
  const filterWrapperRef = useRef(null);
  const filterModalRef = useRef(null);
  const filterButtonRef = useRef(null);
  const previousActiveElementRef = useRef(null);

  const closeFilters = useCallback(() => {
    setShowFilters(false);
  }, []);

  useEffect(() => {
    const handleClickOutside = event => {
      if (
        filterWrapperRef.current &&
        !filterWrapperRef.current.contains(event.target)
      ) {
        closeFilters();
      }
    };

    if (showFilters) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFilters, closeFilters]);

  useEffect(() => {
    if (showFilters) {
      previousActiveElementRef.current = document.activeElement;

      const modal = filterModalRef.current;
      if (!modal) return;

      const focusableSelectors =
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
      const focusableElements = Array.from(modal.querySelectorAll(focusableSelectors));

      const firstElement = focusableElements[0] || modal;
      const lastElement = focusableElements[focusableElements.length - 1] || modal;

      const handleKeyDown = event => {
        if (event.key === 'Escape') {
          event.preventDefault();
          closeFilters();
        } else if (event.key === 'Tab' && focusableElements.length > 0) {
          if (event.shiftKey) {
            if (document.activeElement === firstElement) {
              event.preventDefault();
              lastElement.focus();
            }
          } else if (document.activeElement === lastElement) {
            event.preventDefault();
            firstElement.focus();
          }
        }
      };

      modal.addEventListener('keydown', handleKeyDown);

      requestAnimationFrame(() => {
        firstElement.focus();
      });

      return () => {
        modal.removeEventListener('keydown', handleKeyDown);
      };
    }

    const returnFocusTarget = filterButtonRef.current || previousActiveElementRef.current;
    if (returnFocusTarget && returnFocusTarget.focus) {
      returnFocusTarget.focus();
    }
  }, [showFilters, closeFilters]);

  const toggleIssue = useCallback(issueId => {
    setExpandedIssues(prev => ({
      ...prev,
      [issueId]: !prev[issueId],
    }));
  }, []);

  const handleTimestampClick = useCallback(
    (timestampStr, e) => {
      e.stopPropagation();
      const seconds = parseBracketTimestamp(timestampStr);
      if (seconds !== null && onSeek) {
        onSeek(seconds);
      }
    },
    [onSeek],
  );

  // Get unique values for filters
  const domains = React.useMemo(() => {
    if (!aiAnalysisData?.issues) return [];
    const uniqueDomains = [...new Set(aiAnalysisData.issues.map(issue => issue.domain))];
    return uniqueDomains.sort();
  }, [aiAnalysisData]);

  // Group themes by domain (hierarchical: Domain → Theme)
  const themesByDomain = React.useMemo(() => {
    if (!aiAnalysisData?.issues) return {};

    const grouped = {};
    aiAnalysisData.issues.forEach(issue => {
      if (!issue.domain || !issue.theme) return;

      if (!grouped[issue.domain]) {
        grouped[issue.domain] = new Set();
      }
      grouped[issue.domain].add(issue.theme);
    });

    // Convert Sets to sorted arrays
    const result = {};
    Object.keys(grouped)
      .sort()
      .forEach(domain => {
        result[domain] = Array.from(grouped[domain]).sort();
      });

    return result;
  }, [aiAnalysisData]);

  const severities = React.useMemo(() => {
    if (!aiAnalysisData?.issues) return [];
    const uniqueSeverities = [
      ...new Set(aiAnalysisData.issues.map(issue => issue.severity)),
    ];
    const severityOrder = {
      High: 0,
      Medium: 1,
      Low: 2,
    };

    return uniqueSeverities.sort((a, b) => {
      const orderA = severityOrder[a] ?? Number.MAX_SAFE_INTEGER;
      const orderB = severityOrder[b] ?? Number.MAX_SAFE_INTEGER;
      return orderA - orderB;
    });
  }, [aiAnalysisData]);

  const analysisPasses = React.useMemo(() => {
    if (!aiAnalysisData?.issues) return [];
    const uniquePasses = [
      ...new Set(aiAnalysisData.issues.map(issue => issue.analysisPass)),
    ];
    return uniquePasses.sort();
  }, [aiAnalysisData]);

  // Filter issues
  const filteredIssues = React.useMemo(() => {
    if (!aiAnalysisData?.issues) return [];

    const severityOrder = {
      High: 0,
      Medium: 1,
      Low: 2,
    };

    return aiAnalysisData.issues
      .filter(issue => {
        // Domain filter
        if (selectedDomains.length > 0 && !selectedDomains.includes(issue.domain)) {
          return false;
        }

        // Theme filter
        if (selectedThemes.length > 0 && !selectedThemes.includes(issue.theme)) {
          return false;
        }

        // Severity filter
        if (selectedSeverities.length > 0 && !selectedSeverities.includes(issue.severity)) {
          return false;
        }

        // Analysis Pass filter
        if (selectedAnalysisPass && issue.analysisPass !== selectedAnalysisPass) {
          return false;
        }

        return true;
      })
      .sort((issueA, issueB) => {
        const severityA = severityOrder[issueA.severity] ?? Number.MAX_SAFE_INTEGER;
        const severityB = severityOrder[issueB.severity] ?? Number.MAX_SAFE_INTEGER;

        if (severityA !== severityB) {
          return severityA - severityB;
        }

        const timeA = parseBracketTimestamp(issueA.timestamp);
        const timeB = parseBracketTimestamp(issueB.timestamp);

        if (timeA !== null && timeB !== null) {
          return timeA - timeB;
        }

        if (timeA !== null) {
          return -1;
        }

        if (timeB !== null) {
          return 1;
        }

        return 0;
      });
  }, [
    aiAnalysisData,
    selectedDomains,
    selectedThemes,
    selectedSeverities,
    selectedAnalysisPass,
  ]);

  // Get filter counts
  const getDomainCount = domain => {
    return aiAnalysisData?.issues?.filter(issue => issue.domain === domain).length || 0;
  };

  const getThemeCount = (theme, domain) => {
    return (
      aiAnalysisData?.issues?.filter(
        issue => issue.theme === theme && issue.domain === domain,
      ).length || 0
    );
  };

  const getSeverityCount = severity => {
    return (
      aiAnalysisData?.issues?.filter(issue => issue.severity === severity).length || 0
    );
  };

  const handleToggleDomain = domain => {
    setSelectedDomains(prev => {
      const newDomains = prev.includes(domain)
        ? prev.filter(d => d !== domain)
        : [...prev, domain];

      // When domain selection changes, filter out themes that are no longer valid
      if (newDomains.length > 0) {
        const validThemes =
          aiAnalysisData?.issues
            ?.filter(issue => newDomains.includes(issue.domain))
            .map(issue => issue.theme) || [];
        const validThemeSet = new Set(validThemes);

        setSelectedThemes(prevThemes =>
          prevThemes.filter(theme => validThemeSet.has(theme)),
        );
      }

      return newDomains;
    });
  };

  const handleToggleTheme = theme => {
    setSelectedThemes(prev =>
      prev.includes(theme) ? prev.filter(t => t !== theme) : [...prev, theme],
    );
  };

  const handleToggleSeverity = severity => {
    setSelectedSeverities(prev =>
      prev.includes(severity) ? prev.filter(s => s !== severity) : [...prev, severity],
    );
  };

  const handleToggleDomainExpansion = useCallback(domain => {
    setExpandedDomains(prev => ({
      ...prev,
      [domain]: !prev[domain],
    }));
  }, []);

  const handleToggleFilters = useCallback(() => {
    setShowFilters(prev => !prev);
  }, []);

  const handleAnalysisPassChange = useCallback(e => {
    setSelectedAnalysisPass(e.target.value);
  }, []);

  const handleStopPropagation = useCallback(e => {
    e.stopPropagation();
  }, []);

  const handleClearFilters = useCallback(() => {
    setSelectedDomains([]);
    setSelectedThemes([]);
    setSelectedSeverities([]);
    setSelectedAnalysisPass('');
  }, []);

  const activeFilterCount =
    selectedDomains.length +
    selectedThemes.length +
    selectedSeverities.length +
    (selectedAnalysisPass ? 1 : 0);

  const handleExport = () => {
    const text = (filteredIssues || [])
      .map(issue =>
        [
          `${issue.timestamp} [${issue.speaker}] [${issue.severity}]`,
          `Theme: ${issue.theme}`,
          `Domain: ${issue.domain} | Analysis Pass: ${issue.analysisPass}`,
          `Quote: "${issue.quote}"`,
          `Context: ${issue.context}`,
          `Issue: ${issue.justification}`,
          `Suggested Alternative: ${issue.alternative}`,
        ].join('\n'),
      )
      .join('\n\n---\n\n');
    const date = demoDate || new Date().toISOString().split('T')[0];
    downloadTextFile(`aiAnalysis-${classroomId}-${date}.txt`, text);
  };

  if (isLoading || isProcessing) {
    return null; // Loading/processing handled by parent
  }

  if (!aiAnalysisData || !aiAnalysisData.issues || aiAnalysisData.issues.length === 0) {
    return (
      <EmptyState>
        <Text variant="body1Regular" style={{ marginBottom: '8px' }}>
          No AI analysis available
        </Text>
        <Text variant="body2Regular" style={{ marginBottom: '16px' }}>
          {canGenerateAIAnalysis
            ? 'Click the button below to generate AI analysis for this trial.'
            : 'You do not have permission to generate AI analysis.'}
        </Text>
        {onGenerateAIAnalysis && (
          <Button
            onClick={onGenerateAIAnalysis}
            disabled={!canGenerateAIAnalysis}
            title={
              !canGenerateAIAnalysis
                ? 'You do not have permission to generate AI analysis'
                : undefined
            }
            style={{
              background: canGenerateAIAnalysis ? COLORS.COLA : '#cccccc',
              color: COLORS.WHITE,
              padding: '8px 16px',
              fontSize: '12px',
              cursor: canGenerateAIAnalysis ? 'pointer' : 'not-allowed',
              opacity: canGenerateAIAnalysis ? 1 : 0.6,
            }}
          >
            ➕ Generate AI Analysis
          </Button>
        )}
      </EmptyState>
    );
  }

  return (
    <PanelContainer>
      <FilterWrapper ref={filterWrapperRef}>
        <FilterButtonContainer>
          <ExportButton onClick={handleExport} aria-label="Export AI analysis">
            &#8595; Export
          </ExportButton>
          <FilterButton
            onClick={handleToggleFilters}
            aria-label="Filter issues"
            ref={filterButtonRef}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
            </svg>
            Filter {activeFilterCount > 0 && `(${activeFilterCount})`}
          </FilterButton>
        </FilterButtonContainer>

        {showFilters && (
          <FilterModal
            ref={filterModalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={FILTER_DIALOG_TITLE_ID}
            tabIndex={-1}
          >
            <FilterHeader>
              <FilterTitle id={FILTER_DIALOG_TITLE_ID}>Filter Issues</FilterTitle>
              <ClearButton onClick={handleClearFilters}>Clear</ClearButton>
            </FilterHeader>

            {/* Analysis Pass Filter */}
            <FilterCategory>
              <FilterLabel>Analysis Pass</FilterLabel>
              <FullWidthSelect
                value={selectedAnalysisPass}
                onChange={handleAnalysisPassChange}
              >
                <option value="">All Analysis Passes</option>
                {analysisPasses.map(pass => (
                  <option key={pass} value={pass}>
                    {pass}
                  </option>
                ))}
              </FullWidthSelect>
            </FilterCategory>

            {/* Domain & Theme Filter (Nested) */}
            <FilterCategory>
              <FilterCategoryHeader>
                <FilterCategoryTitle>Domain & Theme</FilterCategoryTitle>
              </FilterCategoryHeader>
              {domains.map(domain => {
                const domainThemes = themesByDomain[domain] || [];
                const isDomainSelected = selectedDomains.includes(domain);
                const hasSelectedDomains = selectedDomains.length > 0;
                const isExpanded = expandedDomains[domain] !== false; // Default to expanded

                // If domains are selected, only show themes from selected domains
                const shouldShowThemes = !hasSelectedDomains || isDomainSelected;

                return (
                  <DomainContainer key={domain}>
                    <DomainHeader onClick={() => handleToggleDomainExpansion(domain)}>
                      <ToggleIcon expanded={isExpanded} />
                      <DomainFilterOption onClick={handleStopPropagation}>
                        <FilterCheckbox
                          type="checkbox"
                          checked={isDomainSelected}
                          onChange={() => handleToggleDomain(domain)}
                        />
                        <DomainLabel>{domain}</DomainLabel>
                        <FilterCount>({getDomainCount(domain)})</FilterCount>
                      </DomainFilterOption>
                    </DomainHeader>
                    {isExpanded && shouldShowThemes && domainThemes.length > 0 && (
                      <ThemeContainer>
                        {domainThemes.map(theme => (
                          <FilterOption key={`${domain}-${theme}`}>
                            <FilterCheckbox
                              type="checkbox"
                              checked={selectedThemes.includes(theme)}
                              onChange={() => handleToggleTheme(theme)}
                            />
                            <span>{theme}</span>
                            <FilterCount>({getThemeCount(theme, domain)})</FilterCount>
                          </FilterOption>
                        ))}
                      </ThemeContainer>
                    )}
                  </DomainContainer>
                );
              })}
            </FilterCategory>

            {/* Severity Filter */}
            <FilterCategory>
              <FilterCategoryHeader>
                <FilterCategoryTitle>Severity</FilterCategoryTitle>
              </FilterCategoryHeader>
              {severities.map(severity => (
                <FilterOption key={severity}>
                  <FilterCheckbox
                    type="checkbox"
                    checked={selectedSeverities.includes(severity)}
                    onChange={() => handleToggleSeverity(severity)}
                  />
                  <span>{severity}</span>
                  <FilterCount>({getSeverityCount(severity)})</FilterCount>
                </FilterOption>
              ))}
            </FilterCategory>
          </FilterModal>
        )}
      </FilterWrapper>

      <div>
        {filteredIssues.map((issue, index) => {
          const issueId = `${issue.timestamp}-${issue.theme}-${issue.speaker}-${index}`;
          const timestampSeconds = parseBracketTimestamp(issue.timestamp);
          const isActive =
            timestampSeconds !== null && Math.abs(videoTimeSec - timestampSeconds) <= 5;
          const isExpanded = expandedIssues[issueId];

          return (
            <ActiveIssueCard
              key={issueId}
              onClick={() => toggleIssue(issueId)}
              isActive={isActive}
            >
              <IssueHeader>
                <TimestampButton
                  onClick={e => handleTimestampClick(issue.timestamp, e)}
                  disabled={timestampSeconds === null}
                >
                  {issue.timestamp}
                </TimestampButton>
                <LabelBadge type="speaker">{issue.speaker}</LabelBadge>
                <LabelBadge severity={issue.severity}>{issue.severity}</LabelBadge>
              </IssueHeader>

              <ThemeBadge>{issue.theme}</ThemeBadge>

              <IssueContent expanded={isExpanded}>
                <ContentSection>
                  <ContentLabel>Quote</ContentLabel>
                  <ContentText>{issue.quote}</ContentText>
                </ContentSection>

                <ContentSection>
                  <ContentLabel>Context</ContentLabel>
                  <ContentText>{issue.context}</ContentText>
                </ContentSection>

                <ContentSection>
                  <ContentLabel>Issue</ContentLabel>
                  <ContentText>{issue.justification}</ContentText>
                </ContentSection>

                <ContentSection>
                  <ContentLabel>Suggested Alternative</ContentLabel>
                  <AlternativeText>{issue.alternative}</AlternativeText>
                </ContentSection>

                <BadgesContainer>
                  <LabelBadge>{issue.domain}</LabelBadge>
                  <LabelBadge>{issue.analysisPass}</LabelBadge>
                </BadgesContainer>
              </IssueContent>
            </ActiveIssueCard>
          );
        })}
      </div>
    </PanelContainer>
  );
}

AIAnalysisPanel.propTypes = {
  aiAnalysisData: PropTypes.shape({
    workflowId: PropTypes.string,
    workflowTitle: PropTypes.string,
    issues: PropTypes.arrayOf(
      PropTypes.shape({
        timestamp: PropTypes.string.isRequired,
        speaker: PropTypes.string.isRequired,
        theme: PropTypes.string.isRequired,
        severity: PropTypes.string.isRequired,
        quote: PropTypes.string.isRequired,
        context: PropTypes.string.isRequired,
        justification: PropTypes.string.isRequired,
        alternative: PropTypes.string.isRequired,
        analysisPass: PropTypes.string.isRequired,
        domain: PropTypes.string.isRequired,
      }),
    ),
  }),
  videoTimeSec: PropTypes.number.isRequired,
  onSeek: PropTypes.func.isRequired,
  onGenerateAIAnalysis: PropTypes.func,
  isLoading: PropTypes.bool,
  isProcessing: PropTypes.bool,
  canGenerateAIAnalysis: PropTypes.bool,
  classroomId: PropTypes.string,
  demoDate: PropTypes.string,
};

AIAnalysisPanel.defaultProps = {
  aiAnalysisData: null,
  onGenerateAIAnalysis: null,
  isLoading: false,
  isProcessing: false,
  canGenerateAIAnalysis: true,
  classroomId: '',
  demoDate: '',
};

export default AIAnalysisPanel;
